import { AuthenticatedError } from "@bootstrap";
import { useEffect, useState } from "react";
import { type ErrorResponse, Navigate, useRouteError } from "react-router";
// Guarded accessor, NOT @posthog/react's usePostHog: this boundary is in the
// entry chunk, and @posthog/react statically imports posthog-js — importing it
// here would pull the SDK back into the entry bundle (SD-10530). Null until
// the lazily-loaded SDK initializes; pre-init captures were dropped anyway
// (uninitialized + opted-out default), so skipping them is behavior-identical.
import { getPostHog } from "@/analytics/posthog";
import { checkAuth } from "./authLoader";
import { ChunkUpdatingNotice } from "./ChunkUpdatingNotice";
import {
  isChunkLoadError,
  isReloadThrottled,
  recoverFromChunkLoadError,
} from "./chunkLoadRecovery";
import {
  CHUNK_RECOVERY_EVENT,
  collectChunkRecoveryProps,
} from "./chunkRecoveryTelemetry";

/**
 * Error boundary for the auth-gated route — and the nearest boundary above the
 * lazy `AuthenticatedShell` route, so it (not RouteErrorBoundary, which sits
 * *below* the shell) catches a stale shell chunk failing to load.
 *
 * A stale shell chunk (older tab vs. a newer deploy) is recovered by reloading
 * onto the current asset graph, same as the other route boundaries; otherwise
 * an expired session redirects to login and anything else shows the error page.
 */
export const AuthErrorBoundary = () => {
  const error = useRouteError() as ErrorResponse & Error;
  const message = error instanceof Error ? error.message : "";
  const chunkError = isChunkLoadError(message);
  const authed = checkAuth();
  // Seeded from the pure throttle check so an already-exhausted chunk error
  // renders the real error immediately instead of flashing "Updating…".
  const [recoveryExhausted, setRecoveryExhausted] = useState(
    () => chunkError && isReloadThrottled(),
  );

  useEffect(() => {
    // Only recover when the session is valid; otherwise we redirect to login.
    // No explicit Sentry capture on exhaustion here (unlike RootErrorBoundary /
    // RouteErrorBoundary, which render ErrorInfo): when recovery is exhausted we
    // fall through to <AuthenticatedError>, which already reports the error to
    // Sentry in its own effect — capturing here too would double-report it.
    if (chunkError && authed) {
      // Measure the attempt either way (send_instantly: a successful recovery
      // has already queued the reload); exception reporting on exhaustion
      // stays delegated to <AuthenticatedError> per the comment above.
      const recovered = recoverFromChunkLoadError();
      getPostHog()?.capture(
        CHUNK_RECOVERY_EVENT,
        collectChunkRecoveryProps(
          "auth",
          recovered ? "reload" : "exhausted",
          message,
        ),
        { send_instantly: true },
      );
      if (!recovered) {
        setRecoveryExhausted(true);
      }
    }
  }, [chunkError, authed, message]);

  if (error?.status === 401 || !authed) {
    return <Navigate to="/login" replace />;
  }

  if (chunkError && !recoveryExhausted) {
    return <ChunkUpdatingNotice fullScreen />;
  }

  return <AuthenticatedError error={error} />;
};
